// Layout.js
import React, { useState, useEffect, useContext } from 'react';
import NavigationBar from './NavigationBar';
import { AppBar, Toolbar, Typography, IconButton, Box, Badge, Menu, MenuItem, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button } from '@mui/material';
import { Notifications, Logout, BugReport, Close } from '@mui/icons-material';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { UserContext } from '../context/UserContext';
import { createPopup } from '../utils/popupUtils';

const Layout = ({ children, pageTitle, noPadding = false}) => {
  const { user, userInfo } = useContext(UserContext);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  const [bugPage, setBugPage] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugScreenshots, setBugScreenshots] = useState([null, null, null]); // Array for 3 screenshots
  const [bugScreenshotPreviews, setBugScreenshotPreviews] = useState([null, null, null]); // For previews

  useEffect(() => {
    if (userInfo?.profilePictureUrl) {
      const img = new Image();
      img.src = userInfo.profilePictureUrl;
    }
  }, [userInfo?.profilePictureUrl]);

  useEffect(() => {
    if (user) {
      const fetchPendingRequests = async () => {
        try {
          const userRef = doc(db, "UserInformation", user.uid);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();

          if (userData && userData.friendRequests) {
            const requests = userData.friendRequests;
            setPendingRequests(requests);
            console.log(requests);
          }
        } catch (error) {
          console.error("Error fetching pending requests:", error);
        }
      };

      fetchPendingRequests();
    }
  }, [user]);

  const handleBugSubmit = async () => {
    if (!bugPage || !bugDescription) {
      createPopup("Please fill all fields!", "error");
      return;
    }

    try {
      // Upload screenshots and get URLs
      const screenshotUrls = [];
      const storage = getStorage();
      
      for (let i = 0; i < bugScreenshots.length; i++) {
        if (bugScreenshots[i]) {
          const timestamp = new Date().getTime();
          const storageRef = ref(storage, `bug_reports/${user.uid}/${timestamp}_${i}.jpg`);
          
          await uploadBytes(storageRef, bugScreenshots[i]);
          const imageUrl = await getDownloadURL(storageRef);
          screenshotUrls.push(imageUrl);
        }
      }

      await addDoc(collection(db, 'bug_reports'), {
        userId: user.uid,
        page: bugPage,
        description: bugDescription,
        screenshotUrls: screenshotUrls,
        timestamp: serverTimestamp(),
      });
      
      setBugDialogOpen(false);
      setBugPage('');
      setBugDescription('');
      setBugScreenshots([null, null, null]);
      setBugScreenshotPreviews([null, null, null]);
      createPopup("Bug report submitted!", "success");
    } catch (error) {
      console.error("Error submitting bug report:", error);
      createPopup("Failed to submit bug report!", "error");
    }
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleScreenshotChange = (event, index) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        createPopup("File size should be less than 5MB", "error");
        // Reset the input value so the same file can be selected again
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newScreenshots = [...bugScreenshots];
        const newPreviews = [...bugScreenshotPreviews];
        newScreenshots[index] = file;
        newPreviews[index] = e.target.result;
        setBugScreenshots(newScreenshots);
        setBugScreenshotPreviews(newPreviews);
        // Reset the input value so the same file can be selected again
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = (index) => {
    const newScreenshots = [...bugScreenshots];
    const newPreviews = [...bugScreenshotPreviews];
    newScreenshots[index] = null;
    newPreviews[index] = null;
    setBugScreenshots(newScreenshots);
    setBugScreenshotPreviews(newPreviews);
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        console.log("User signed out successfully.");
        navigate('/login');
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  const acceptRequest = async (request) => {
    try {
      const senderRef = doc(db, 'UserInformation', request.senderId);
      const receiverRef = doc(db, 'UserInformation', user.uid);

      const receiverDoc = await getDoc(receiverRef);
      const receiverData = receiverDoc.data();
      const receiverName = receiverData ? receiverData.name : 'Unknown';
      const senderName = request.senderName;

      await updateDoc(senderRef, {
        friendsList: arrayUnion({
          id: user.uid,
          name: receiverName,
      }),
        friends: increment(1)
      });

      await updateDoc(receiverRef, {
        friendsList: arrayUnion({
          id: request.senderId,
          name: senderName, 
      }),
        friends: increment(1),
        friendRequests: arrayRemove({ senderId: request.senderId, senderName: request.senderName, timestamp: request.timestamp }),
      });

      setPendingRequests(pendingRequests.filter((req) => req.senderId !== request.senderId));
      console.log('Friend request accepted');
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const rejectRequest = async (request) => {
    try {
      const receiverRef = doc(db, 'UserInformation', user.uid);

      // Remove the friend request from receiver's friendRequests
      await updateDoc(receiverRef, {
        friendRequests: arrayRemove({ senderId: request.senderId, senderName: request.senderName, timestamp: request.timestamp }),
      });

      setPendingRequests(pendingRequests.filter((req) => req.senderId !== request.senderId));
      console.log('Friend request rejected');
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      width: '100%', 
      height: '100vh',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      flexDirection: 'column',
      '@media (min-width: 1151px)': {
        flexDirection: 'row',
      },
      // Tablets: fit the visible viewport (100vh extends under the browser toolbars)
      '.is-tablet &': {
        height: 'auto',
        bottom: 0,
      },
    }}>
      <NavigationBar 
        onBugReportClick={() => setBugDialogOpen(true)}
        onNotificationClick={handleNotificationClick}
        onLogout={handleLogout}
        pendingRequests={pendingRequests}
        onAcceptRequest={acceptRequest}
        onRejectRequest={rejectRequest}
      />
      <Box 
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          marginLeft: 0,
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          order: 1, // Content comes first on mobile (nav has order: 2)
          '@media (min-width: 1151px)': {
            marginLeft: '260px',
            width: 'calc(100% - 260px)',
            order: 0, // Reset order on desktop
          },
        }}
      >
        <AppBar position="static" color="transparent" elevation={0} sx={{
    zIndex: 999,
    backgroundColor: 'white',
    display: 'none',
    flexShrink: 0,
    '@media (min-width: 1151px)': {
      display: noPadding ? 'none' : 'block',
    }
  }}>
          <Toolbar sx={{ justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{ 
                  fontFamily: 'Bilgres-Regular',
                  fontSize: 30,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '109.2%',
                  color: '#272657',
                  marginTop: '20px',
                }}
              >
                {pageTitle}
              </Typography>

              <IconButton color="inherit" onClick={() => setBugDialogOpen(true)}>
                <BugReport />
              </IconButton>
              <IconButton color="inherit" onClick={handleNotificationClick}>
                <Notifications />
                {pendingRequests.length > 0 && (
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-dot': {
                        backgroundColor: 'red',
                        width: 6,
                        height: 6,
                      },
                    }}
                  />
                )}
              </IconButton>
              <IconButton 
                color="inherit"
                sx={{ ml: 1 }}
                onClick={handleLogout}
              >
                <Logout />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Dialog open={bugDialogOpen} onClose={() => setBugDialogOpen(false)}>
          <DialogTitle>Report a Bug</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Page"
              fullWidth
              value={bugPage}
              onChange={(e) => setBugPage(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={bugDescription}
              onChange={(e) => setBugDescription(e.target.value)}
            />
            
            {/* Screenshot Upload Section */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Screenshots (Max 3)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[0, 1, 2].map((index) => (
                  <Box key={index}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id={`screenshot-input-${index}`}
                      type="file"
                      onChange={(e) => handleScreenshotChange(e, index)}
                    />
                    {bugScreenshotPreviews[index] ? (
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <img 
                          src={bugScreenshotPreviews[index]} 
                          alt={`Screenshot ${index + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            border: '1px solid #ddd'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeScreenshot(index)}
                          sx={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <label htmlFor={`screenshot-input-${index}`}>
                        <Button
                          component="span"
                          variant="outlined"
                          fullWidth
                          sx={{ textTransform: 'none' }}
                        >
                          Upload Screenshot {index + 1}
                        </Button>
                      </label>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setBugDialogOpen(false);
              setBugPage('');
              setBugDescription('');
              setBugScreenshots([null, null, null]);
              setBugScreenshotPreviews([null, null, null]);
            }}>Cancel</Button>
            <Button onClick={handleBugSubmit} variant="contained">Submit</Button>
          </DialogActions>
        </Dialog>

        {/* Dropdown menu for friend requests */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleNotificationClose}
        >
          {pendingRequests.length > 0 ? (
            pendingRequests.map((request, index) => (
              <MenuItem key={index}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>{request.senderName} sent you a friend request.</Typography>
                  <Box>
                    <button onClick={() => {acceptRequest(request)}}>Accept</button>
                    <button onClick={() => {rejectRequest(request)}}>Reject</button>
                  </Box>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem>No new requests</MenuItem>
          )}
        </Menu>

        

        <Box 
          component="main"
          sx={{
            flexGrow: 1,
            padding: noPadding ? 0 : '12px',
            paddingTop: noPadding ? 0 : '12px',
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: noPadding ? 'hidden' : 'auto',
            '@media (min-width: 768px)': {
              padding: noPadding ? 0 : '4px',
              paddingTop: noPadding ? 0 : '4px',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
